import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Phone, MapPin, CheckCircle, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import FAQSection from "@/components/FAQSection";
import { BUSINESS, CITIES, MEDIA } from "@/constants/business";
import { cityFaqs } from "@/constants/faqs";

const areas = {
  bangalore: [
    "Whitefield", "Electronic City", "Koramangala", "Indiranagar", "HSR Layout", "Jayanagar",
    "JP Nagar", "Bannerghatta Road", "Sarjapur Road", "Marathahalli", "Yelahanka", "Hebbal",
    "Bommasandra", "Bommanahalli", "Rajajinagar", "Basavanagudi",
  ],
  chennai: ["T. Nagar", "Adyar", "Anna Nagar", "Velachery", "OMR", "Porur", "Nungambakkam", "Chromepet"],
  hyderabad: ["Gachibowli", "Hitec City", "Banjara Hills", "Jubilee Hills", "Kondapur", "Madhapur", "Kukatpally", "Miyapur"],
  vijayawada: ["Benz Circle", "Governorpet", "Bhavanipuram", "Autonagar", "Patamata", "Gunadala"],
  coimbatore: ["RS Puram", "Peelamedu", "Saibaba Colony", "Race Course", "Gandhipuram"],
  mysuru: ["Vijayanagar", "Kuvempunagar", "Gokulam", "Yadavagiri", "Saraswathipuram"],
  mangaluru: ["Kadri", "Balmatta", "Bejai", "Kankanady", "Bunder", "Falnir"],
  hubballi: ["Vidyanagar", "Gokul Road", "Deshpande Nagar", "Keshwapur", "Navanagar", "Dharwad"],
  belagavi: ["Tilakwadi", "Shahapur", "Camp", "Hindwadi", "Sadashiv Nagar", "Udyambag"],
  tumakuru: ["SS Puram", "Batawadi", "Kyathsandra", "Sira Gate", "Antharasanahalli"],
  shivamogga: ["Vinoba Nagar", "Durgigudi", "Gopala Gowda Extension", "Sagar Road", "Bhadravathi"],
  udupi: ["Manipal", "Malpe", "Ambalpady", "Santhekatte", "Brahmavar", "Kaup"],
  visakhapatnam: ["Dwaraka Nagar", "MVP Colony", "Madhurawada", "Gajuwaka", "Rushikonda"],
};

export default function CityPage({ slug }) {
  const city = CITIES.find((c) => c.slug === slug);

  if (!city) return <Navigate to="/" replace />;

  const cityAreas = areas[city.slug] || [];
  const cityKeywordPhrase = `elevators in ${city.name}, escalators in ${city.name}, lift manufacturer ${city.name}, elevator installation ${city.name}, ${city.name} elevator company, ${city.name} lift service, ${city.state} elevator manufacturer`;

  return (
    <>
      <SEO
        title={`Elevators & Escalators in ${city.name} | Vila – Manufacturer & Installer`}
        description={`Vila Elevators & Escalators serves ${city.name}, ${city.state} with passenger lifts, home lifts, freight elevators & escalators. Installation, AMC & modernization. Call ${BUSINESS.phone}.`}
        path={`/elevators-${city.slug}`}
        keywords={cityKeywordPhrase}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `https://vilaelevators.in/elevators-${city.slug}#business`,
          name: `${BUSINESS.name} — ${city.name}`,
          url: `https://vilaelevators.in/elevators-${city.slug}`,
          telephone: BUSINESS.phoneRaw,
          email: BUSINESS.email,
          areaServed: { "@type": "City", name: city.name, containedInPlace: { "@type": "State", name: city.state } },
          address: {
            "@type": "PostalAddress",
            streetAddress: `${BUSINESS.address.line1}, ${BUSINESS.address.line2}`,
            addressLocality: BUSINESS.address.city,
            addressRegion: BUSINESS.address.state,
            postalCode: BUSINESS.address.postal,
            addressCountry: "IN",
          },
        }}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 border-b border-[#e5ddd0] overflow-hidden">
        <img src={MEDIA.hero} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#faf7f2] via-[#faf7f2]/85 to-[#faf7f2]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4 flex items-center gap-2">
            <MapPin className="w-3 h-3" /> Serving {city.state}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#1a1a1a] leading-[0.95] max-w-4xl">
            Elevators & Escalators{" "}<br />
            <span className="text-[#b08d57]">in {city.name}.</span>
          </h1>
          <p className="text-[#6b6b6b] text-lg max-w-2xl mt-6 leading-relaxed">
            Vila Elevators & Escalators is proud to serve builders, architects and facility managers across {city.name}. From passenger lifts in high-rises to home lifts in villas — we deliver, install and maintain across the city.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid={`city-cta-quote-${city.slug}`} className="bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center gap-2">
              Get {city.name} Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="border-2 border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] text-[#1a1a1a] px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Services in this city */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Our Coverage</div>
              <h2 className="font-display text-4xl font-bold text-[#1a1a1a] mb-6">
                Complete elevator services across {city.name}.
              </h2>
              <p className="text-[#6b6b6b] text-lg leading-relaxed mb-8">
                Whether you're building a new residential tower, upgrading a hospital, or modernizing an old commercial building — our {city.name} operations team handles the full cycle from consultation to lifetime maintenance.
              </p>
              <ul className="space-y-3">
                {[
                  `Passenger elevator installation across ${city.name}`,
                  "Home lifts for villas & duplexes",
                  "Freight & hospital elevators",
                  "Escalators for malls, transit hubs & offices",
                  "Annual Maintenance Contracts (AMC)",
                  "Modernization of legacy lifts",
                  "24×7 emergency breakdown response",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-3 text-[#4a4a4a]">
                    <CheckCircle className="w-5 h-5 text-[#b08d57] flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-[#e5ddd0] bg-[#faf7f2]/50 p-8 lg:p-10">
              <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-2">Get a {city.name} quote</h3>
              <p className="text-[#6b6b6b] text-sm mb-6">Fill in the form — we'll respond within one business day.</p>
              <ContactForm sourcePage={`city-${city.slug}`} defaultCity={city.name} />
            </div>
          </div>
        </div>
      </section>

      {/* Areas served */}
      {cityAreas.length > 0 && (
        <section className="py-20 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Neighbourhoods Served</div>
            <h2 className="font-display text-3xl font-bold text-[#1a1a1a] mb-8">
              Elevator services across {city.name} localities.
            </h2>
            <div className="flex flex-wrap gap-3">
              {cityAreas.map((a) => (
                <span key={a} className="border border-[#e5ddd0] px-4 py-2 text-sm text-[#4a4a4a] hover:border-[#b08d57]/50 transition-colors">
                  {a}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ — AEO */}
      <FAQSection
        faqs={cityFaqs(city)}
        title={`Elevators in ${city.name} — common questions`}
      />

      {/* Other cities */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Also Serving</div>
          <h3 className="font-display text-3xl font-bold text-[#1a1a1a] mb-8">Other cities we cover</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
              <Link
                key={c.slug}
                to={`/elevators-${c.slug}`}
                className="border border-[#e5ddd0] hover:border-[#b08d57]/50 p-5 group transition-colors"
              >
                <MapPin className="w-4 h-4 text-[#b08d57] mb-3" />
                <div className="font-display font-bold text-[#1a1a1a]">Elevators in {c.name}</div>
                <div className="text-xs text-[#8a8a8a] uppercase tracking-widest mt-1">{c.state}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
